/** @jsxImportSource @emotion/react */

import { useApolloClient, useMutation } from '@apollo/client'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import MovieCreationRoundedIcon from '@mui/icons-material/MovieCreationRounded'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import { Button, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material'
import { ChangeEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { createVideoReview, createVideoReviewVariables } from '__generatedTypes__/createVideoReview'
import { FileProcessingStatus } from '__generatedTypes__/globalTypes'
import { updateVideoReview, updateVideoReviewVariables } from '__generatedTypes__/updateVideoReview'
import Input from 'components/Input'
import { PlatformSlug } from 'constants/consts'
import { CREATE_VIDEO_REVIEW, UPDATE_VIDEO_REVIEW } from 'queries/videoReview'
import { toast } from 'utils/toast'
import { createUuid } from 'utils/uuid'
import { hydrateVideoReview, isVideoReviewProcessing, uploadReviewAssets } from './api'
import { formatFileSize } from './assetUrls'
import { validateImageFile, validateVideoFile } from './mediaValidation'
import { RealStudentReview, RealStudentReviewAssetRefs } from './types'
import { DEFAULT_THUMBNAIL_SEEK_TIME, extractPosterFromVideo, validateVideoMetadata } from './videoProcessing'
import * as classes from './styles'

type Props = {
  open: boolean
  platformSlug: PlatformSlug
  videoReviewSectionId: string
  review?: RealStudentReview | null
  videoProcessingStatus?: FileProcessingStatus | null
  onClose: () => void
  onSaved: (review: RealStudentReview) => void
}

const VIDEO_REPLACEMENT_UNAVAILABLE_MESSAGE =
  'Wait for the current video to finish processing or fail before uploading a replacement.'

type FilePickerProps = {
  label: string
  helperText: string
  accept: string
  file?: File | null
  currentPath?: string
  onChange: (file: File | null) => void
  icon: JSX.Element
  disabled?: boolean
  previewSrc?: string
  previewAlt?: string
  children?: ReactNode
}

const FilePicker = ({
  label,
  helperText,
  accept,
  file,
  currentPath,
  onChange,
  icon,
  disabled,
  previewSrc,
  previewAlt,
  children,
}: FilePickerProps) => {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.files?.[0] || null)
    event.target.value = ''
  }

  return (
    <div css={classes.filePicker}>
      <div css={classes.filePickerHeader}>
        <Typography variant="body2" css={classes.filePickerTitle}>
          {label}
        </Typography>
        <span css={disabled ? classes.disabledFilePickerButtonWrapper : undefined}>
          <Button
            component="label"
            color="blue"
            variant="outlined"
            size="small"
            disabled={disabled}
            css={classes.filePickerButton}
          >
            {icon}
            Choose File
            <input hidden type="file" accept={accept} onChange={handleChange} />
          </Button>
        </span>
      </div>
      <Typography variant="small1" css={classes.helperText}>
        {helperText}
      </Typography>
      {file ? (
        <Typography variant="small1" css={classes.selectedFile}>
          {file.name} ({formatFileSize(file.size)})
        </Typography>
      ) : currentPath ? (
        <Typography variant="small1" css={classes.pathText}>
          Current: {currentPath}
        </Typography>
      ) : (
        <Typography variant="small1" css={classes.missingAsset}>
          No file selected
        </Typography>
      )}
      {previewSrc && (
        <div css={classes.filePreviewRow}>
          <img src={previewSrc} alt={previewAlt || label} css={classes.filePreviewImage} />
          <Typography variant="small1" css={classes.helperText}>
            Preview
          </Typography>
        </div>
      )}
      {children}
    </div>
  )
}

const getPreviewUrl = (file: File | null) => {
  if (!file) {
    return ''
  }

  return URL.createObjectURL(file)
}

const getImagePathRefs = (refs: RealStudentReviewAssetRefs) => ({
  ...(refs.thumbnail ? { thumbnail: refs.thumbnail } : {}),
  ...(refs.avatar ? { avatar: refs.avatar } : {}),
})

export const ReviewModal = ({
  open,
  platformSlug,
  videoReviewSectionId,
  review,
  videoProcessingStatus,
  onClose,
  onSaved,
}: Props) => {
  const apolloClient = useApolloClient()
  const [createVideoReview] = useMutation<createVideoReview, createVideoReviewVariables>(CREATE_VIDEO_REVIEW)
  const [updateVideoReview] = useMutation<updateVideoReview, updateVideoReviewVariables>(UPDATE_VIDEO_REVIEW)
  const [reviewerName, setReviewerName] = useState('')
  const [reviewerDescription, setReviewerDescription] = useState('')
  const [videoFile, setVideoFile] = useState<File | null>(null)
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null)
  const [videoPreviewUrl, setVideoPreviewUrl] = useState('')
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState('')
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState('')
  const [isValidatingVideo, setIsValidatingVideo] = useState(false)
  const [isPreparingVideo, setIsPreparingVideo] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const isEditing = Boolean(review)
  const modalTitle = isEditing ? 'Edit Review Video' : 'Create Review Video'
  const thumbnailPreviewObjectUrlRef = useRef('')
  const thumbnailExtractionIdRef = useRef(0)
  const videoValidationIdRef = useRef(0)
  const createReviewIdRef = useRef<string | null>(null)

  const clearThumbnailPreviewObjectUrl = useCallback(() => {
    if (!thumbnailPreviewObjectUrlRef.current) {
      return
    }

    URL.revokeObjectURL(thumbnailPreviewObjectUrlRef.current)
    thumbnailPreviewObjectUrlRef.current = ''
  }, [])

  const handleAvatarFileChange = (file: File | null) => {
    const validationError = file ? validateImageFile(file) : ''

    if (validationError) {
      setErrors((errors) => ({ ...errors, avatar: validationError }))
      toast.error(validationError)
      return
    }

    setErrors((errors) => ({ ...errors, avatar: '' }))
    setAvatarFile(file)
  }

  const handleThumbnailFileChange = (file: File | null) => {
    const validationError = file ? validateImageFile(file) : ''

    if (validationError) {
      setErrors((errors) => ({ ...errors, thumbnail: validationError }))
      toast.error(validationError)
      return
    }

    clearThumbnailPreviewObjectUrl()
    setErrors((errors) => ({ ...errors, thumbnail: '' }))
    setThumbnailFile(file)

    if (file) {
      const previewUrl = getPreviewUrl(file)

      thumbnailPreviewObjectUrlRef.current = previewUrl
      setThumbnailPreviewUrl(previewUrl)
      return
    }

    setThumbnailPreviewUrl(review?.thumbnailUrl || '')
  }

  const handleVideoFileChange = async (file: File | null) => {
    const validationId = videoValidationIdRef.current + 1

    videoValidationIdRef.current = validationId

    if (!file) {
      setErrors((errors) => ({ ...errors, video: '' }))
      setVideoFile(null)
      setIsValidatingVideo(false)
      return
    }

    const validationError = validateVideoFile(file)

    if (validationError) {
      setIsValidatingVideo(false)
      setErrors((errors) => ({ ...errors, video: validationError }))
      toast.error(validationError)
      return
    }

    setErrors((errors) => ({ ...errors, video: '' }))
    setIsValidatingVideo(true)

    try {
      await validateVideoMetadata(file)

      if (videoValidationIdRef.current !== validationId) {
        return
      }

      setVideoFile(file)
    } catch (error) {
      if (videoValidationIdRef.current !== validationId) {
        return
      }

      const message = error instanceof Error ? error.message : 'Cannot validate the selected video'

      setErrors((errors) => ({ ...errors, video: message }))
      toast.error(message)
    } finally {
      if (videoValidationIdRef.current === validationId) {
        setIsValidatingVideo(false)
      }
    }
  }

  const applyGeneratedThumbnail = useCallback(
    (file: File) => {
      clearThumbnailPreviewObjectUrl()

      const previewUrl = getPreviewUrl(file)

      thumbnailPreviewObjectUrlRef.current = previewUrl
      setThumbnailFile(file)
      setThumbnailPreviewUrl(previewUrl)
    },
    [clearThumbnailPreviewObjectUrl]
  )

  const extractThumbnail = useCallback(async () => {
    if (!videoFile) {
      return
    }

    const extractionId = thumbnailExtractionIdRef.current + 1
    thumbnailExtractionIdRef.current = extractionId

    setThumbnailFile(null)
    setErrors((errors) => ({ ...errors, thumbnail: '' }))
    setIsPreparingVideo(true)

    try {
      const file = await extractPosterFromVideo(videoFile)

      if (thumbnailExtractionIdRef.current !== extractionId) {
        return
      }

      applyGeneratedThumbnail(file)
    } catch (error) {
      if (thumbnailExtractionIdRef.current !== extractionId) {
        return
      }

      const message = error instanceof Error ? error.message : 'Failed to extract video thumbnail'

      setErrors((errors) => ({ ...errors, thumbnail: message }))
      toast.error(message)
    } finally {
      if (thumbnailExtractionIdRef.current === extractionId) {
        setIsPreparingVideo(false)
      }
    }
  }, [applyGeneratedThumbnail, videoFile])

  useEffect(() => {
    if (!open) {
      createReviewIdRef.current = null
      thumbnailExtractionIdRef.current += 1
      videoValidationIdRef.current += 1
      setIsValidatingVideo(false)
      setIsPreparingVideo(false)
      return
    }

    createReviewIdRef.current = null
    setReviewerName(review?.fullName || '')
    setReviewerDescription(review?.description || '')
    setVideoFile(null)
    setAvatarFile(null)
    setThumbnailFile(null)
    setVideoPreviewUrl(review?.videoUrl || '')
    setThumbnailPreviewUrl(review?.thumbnailUrl || '')
    setAvatarPreviewUrl(review?.avatarUrl || '')
    setIsValidatingVideo(false)
    setIsPreparingVideo(false)
    setIsSubmitting(false)
    setErrors({})
    clearThumbnailPreviewObjectUrl()
    thumbnailExtractionIdRef.current += 1
    videoValidationIdRef.current += 1
  }, [clearThumbnailPreviewObjectUrl, open, review])

  useEffect(() => {
    if (!videoFile) {
      return
    }

    const localVideoPreviewUrl = getPreviewUrl(videoFile)

    thumbnailExtractionIdRef.current += 1
    setVideoPreviewUrl(localVideoPreviewUrl)
    setThumbnailFile(null)
    setThumbnailPreviewUrl('')
    clearThumbnailPreviewObjectUrl()
    setErrors((errors) => ({ ...errors, video: '', thumbnail: '' }))
    extractThumbnail()

    return () => {
      thumbnailExtractionIdRef.current += 1
      URL.revokeObjectURL(localVideoPreviewUrl)
    }
  }, [clearThumbnailPreviewObjectUrl, extractThumbnail, videoFile])

  useEffect(() => {
    if (!avatarFile) {
      return
    }

    const previewUrl = getPreviewUrl(avatarFile)

    setAvatarPreviewUrl(previewUrl)

    return () => URL.revokeObjectURL(previewUrl)
  }, [avatarFile])

  const previewVideoUrl = videoPreviewUrl || review?.videoUrl || ''
  const previewPosterUrl = thumbnailPreviewUrl || review?.thumbnailUrl || ''
  const isSaveDisabled = isSubmitting || isValidatingVideo || isPreparingVideo
  const isVideoReplacementDisabled = Boolean(isEditing && isVideoReviewProcessing(videoProcessingStatus))

  const validate = () => {
    const nextErrors: Record<string, string> = {}

    if (!reviewerName.trim()) {
      nextErrors.reviewerName = 'Required'
    }

    if (!reviewerDescription.trim()) {
      nextErrors.reviewerDescription = 'Required'
    }

    if (!isEditing && !videoFile) {
      nextErrors.video = 'Video is required'
    }

    if (!isEditing && !avatarFile) {
      nextErrors.avatar = 'Reviewer avatar is required'
    }

    if (!isEditing && !thumbnailFile) {
      nextErrors.thumbnail = 'Thumbnail is required'
    }

    if (videoFile && !thumbnailFile) {
      nextErrors.thumbnail = 'Wait until thumbnail extraction is complete'
    }

    setErrors(nextErrors)

    return !Object.keys(nextErrors).length
  }

  const getCreateReviewId = () => {
    const id = createReviewIdRef.current || createUuid()

    createReviewIdRef.current = id

    return id
  }

  const handleSubmit = async () => {
    if (!validate()) {
      return
    }

    if (videoFile && isVideoReplacementDisabled) {
      setErrors((errors) => ({ ...errors, video: VIDEO_REPLACEMENT_UNAVAILABLE_MESSAGE }))
      toast.error(VIDEO_REPLACEMENT_UNAVAILABLE_MESSAGE)
      return
    }

    setIsSubmitting(true)

    try {
      const reviewId = review?.id || getCreateReviewId()
      let assetRefs: RealStudentReviewAssetRefs = {}
      let videoFileName: string | undefined

      if (videoFile || thumbnailFile || avatarFile) {
        const uploadResult = await uploadReviewAssets({
          apolloClient,
          platformSlug,
          reviewId,
          videoFile: videoFile || undefined,
          thumbnailFile: thumbnailFile || undefined,
          avatarFile: avatarFile || undefined,
        })

        assetRefs = uploadResult.refs
        videoFileName = uploadResult.videoFileName
      }

      const commonInput = {
        fullName: reviewerName.trim(),
        description: reviewerDescription.trim(),
      }
      const imagePathRefs = getImagePathRefs(assetRefs)
      const thumbnail = imagePathRefs.thumbnail || review?.thumbnail || ''
      const avatar = imagePathRefs.avatar || review?.avatar || ''
      let savedReview: RealStudentReview | null = null

      if (isEditing) {
        const updateResult = await updateVideoReview({
          variables: {
            updateVideoReviewInputFieldName: {
              id: reviewId,
              ...imagePathRefs,
              ...commonInput,
              ...(videoFileName ? { videoFileName } : {}),
            },
          },
        })

        if (updateResult.data?.updateVideoReview) {
          savedReview = hydrateVideoReview(updateResult.data.updateVideoReview, platformSlug)
        }
      } else {
        if (!videoFileName) {
          throw new Error('Video upload is required before creating a review')
        }

        const createResult = await createVideoReview({
          variables: {
            createVideoReviewInput: {
              id: reviewId,
              videoReviewSection: { id: videoReviewSectionId },
              thumbnail,
              avatar,
              ...commonInput,
              videoFileName,
            },
          },
        })

        if (createResult.data?.createVideoReview) {
          savedReview = hydrateVideoReview(createResult.data.createVideoReview, platformSlug)
        }
      }

      if (!savedReview) {
        throw new Error('Review was saved but no response was returned')
      }

      toast.success(isEditing ? 'Review video updated successfully' : 'Review video created successfully')
      onSaved(savedReview)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save review video')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="md" fullWidth css={classes.dialog}>
      <DialogTitle css={classes.dialogTitle}>
        <span>{modalTitle}</span>
        <IconButton aria-label="Close review dialog" onClick={onClose} disabled={isSubmitting}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent css={classes.dialogContent}>
        <div css={classes.modalGrid}>
          <div css={classes.modalForm}>
            <Input
              value={reviewerName}
              onChange={setReviewerName}
              label="Reviewer name or tag"
              placeholder="Example: @carsstudent"
              fullWidth
              error={Boolean(errors.reviewerName)}
              formHelperText={errors.reviewerName}
              disabled={isSubmitting}
            />
            <Input
              value={reviewerDescription}
              onChange={setReviewerDescription}
              label="Reviewer description"
              placeholder="Example: Improved from 123 to 129"
              fullWidth
              error={Boolean(errors.reviewerDescription)}
              formHelperText={errors.reviewerDescription}
              disabled={isSubmitting}
            />
            <FilePicker
              label="Reviewer avatar"
              helperText="Upload a square JPEG, PNG, or WebP image up to 10 MB."
              accept="image/jpeg,image/png,image/webp"
              file={avatarFile}
              currentPath={review?.avatar}
              onChange={handleAvatarFileChange}
              icon={<UploadFileRoundedIcon />}
              disabled={isSubmitting}
              previewSrc={avatarPreviewUrl}
              previewAlt={reviewerName || 'Reviewer avatar preview'}
            />
            {errors.avatar && (
              <Typography variant="small1" css={classes.errorText}>
                {errors.avatar}
              </Typography>
            )}

            <div css={classes.mediaGrid}>
              <div css={classes.mediaField}>
                <FilePicker
                  label="Video"
                  helperText={
                    isVideoReplacementDisabled
                      ? VIDEO_REPLACEMENT_UNAVAILABLE_MESSAGE
                      : 'Upload an MP4, MOV, M4V, or WebM video up to 500 MB and 5 minutes.'
                  }
                  accept="video/mp4,video/quicktime,video/webm,video/x-m4v"
                  file={videoFile}
                  currentPath={review?.video}
                  onChange={handleVideoFileChange}
                  icon={<MovieCreationRoundedIcon />}
                  disabled={isVideoReplacementDisabled || isSubmitting || isValidatingVideo || isPreparingVideo}
                >
                  {previewVideoUrl && (
                    <div css={classes.inlinePreviewBlock}>
                      <Typography variant="body3" css={classes.inlinePreviewTitle}>
                        Video preview
                      </Typography>
                      {/* Uploaded review videos do not currently include a caption asset for the CMS preview. */}
                      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                      <video
                        src={previewVideoUrl}
                        poster={previewPosterUrl}
                        aria-label="Student review video preview"
                        css={classes.inlineVideoPreview}
                        controls
                        playsInline
                        preload="metadata"
                      />
                    </div>
                  )}
                </FilePicker>
                {errors.video && (
                  <Typography variant="small1" css={classes.errorText}>
                    {errors.video}
                  </Typography>
                )}
              </div>

              <div css={classes.mediaField}>
                <FilePicker
                  label="Thumbnail"
                  helperText={`Generated automatically at ${DEFAULT_THUMBNAIL_SEEK_TIME.toFixed(
                    2
                  )} seconds. Upload a JPEG, PNG, or WebP image up to 10 MB to override it.`}
                  accept="image/jpeg,image/png,image/webp"
                  file={thumbnailFile}
                  currentPath={review?.thumbnail}
                  onChange={handleThumbnailFileChange}
                  icon={<UploadFileRoundedIcon />}
                  disabled={isSubmitting || isValidatingVideo || isPreparingVideo}
                >
                  {previewPosterUrl && (
                    <div css={classes.inlinePreviewBlock}>
                      <Typography variant="body3" css={classes.inlinePreviewTitle}>
                        Thumbnail preview
                      </Typography>
                      <img src={previewPosterUrl} alt="Thumbnail preview" css={classes.inlineImagePreview} />
                    </div>
                  )}
                </FilePicker>
                {errors.thumbnail && (
                  <Typography variant="small1" css={classes.errorText}>
                    {errors.thumbnail}
                  </Typography>
                )}
              </div>
            </div>
          </div>
        </div>

        <div css={classes.modalActions}>
          <Button color="blue" variant="outlined" size="large" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button color="blue" size="large" onClick={handleSubmit} disabled={isSaveDisabled}>
            {isValidatingVideo
              ? 'Validating...'
              : isPreparingVideo
              ? 'Preparing...'
              : isSubmitting
              ? 'Saving...'
              : 'Save Review'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
